import { useState, useEffect, useCallback } from 'react';
import { databases } from '../../../services/appwriteService';
import { Query } from 'appwrite';

const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

export interface DailyReport {
  date: string;
  auditor_id: string;
  auditor_name: string;
  audits_count: number;
  sessions_count: number;
  validated_tasks: number;
  commented_tasks: number;
  pending_tasks: number;
  avg_processing_time: number;
  validation_rate: number;
  productivity_score: number;
}

interface UseDailyReportsProps {
  dateFrom: string;
  dateTo: string;
  auditorId?: string;
}

interface LoadingProgress {
  stage: string;
  progress: number;
  currentBatch?: number;
  totalBatches?: number;
  message: string;
}

export const useDailyReports = ({ dateFrom, dateTo, auditorId }: UseDailyReportsProps) => {
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress>({
    stage: '',
    progress: 0,
    message: ''
  });

  const updateProgress = useCallback((stage: string, progress: number, message: string, currentBatch?: number, totalBatches?: number) => {
    setLoadingProgress({
      stage,
      progress: Math.min(100, Math.max(0, progress)),
      currentBatch,
      totalBatches,
      message
    });
  }, []);

  const loadDailyReports = useCallback(async () => {
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setError(null);
      updateProgress('init', 0, 'Initialisation du chargement...');
      
      console.log('📊 [VRAIS DONNÉES] Chargement des rapports journaliers...', { dateFrom, dateTo, auditorId });

      // 1. Récupérer tous les auditeurs
      updateProgress('auditors', 5, 'Récupération des auditeurs...');
      
      const auditorsResponse = await databases.listDocuments(
        appwriteDatabaseId,
        'users',
        [
          Query.equal('role', 'auditor'),
          Query.select(['$id', 'full_name', 'email'])
        ]
      );

      const auditors = auditorsResponse.documents;
      console.log('👥 [VRAIES DONNÉES] Auditeurs trouvés:', auditors.length);
      
      updateProgress('auditors', 10, `${auditors.length} auditeurs trouvés`);

      // 2. Récupérer toutes les tâches de la période (BATCH DE 500)
      updateProgress('tasks', 15, 'Préparation de la récupération des tâches...');
      
      const tasksQueries = [
        Query.greaterThanEqual('audited_at', dateFrom + 'T00:00:00.000Z'),
        Query.lessThanEqual('audited_at', dateTo + 'T23:59:59.999Z'),
        Query.notEqual('audited_at', ''), // Seulement les tâches auditées
        Query.select(['$id', 'audited_at', 'auditor_id', 'audit_status', 'processing_time_seconds', 'session_id', 'agent_email']) // ✅ CORRIGER audited_by → auditor_id
      ];
      
      if (auditorId && auditorId !== 'all') {
        tasksQueries.push(Query.equal('auditor_id', auditorId)); // ✅ CORRIGER audited_by → auditor_id
      }

      // ✅ RÉCUPÉRATION COMPLÈTE DES TÂCHES (batch de 500)
      let allTasks: any[] = [];
      let offset = 0;
      const batchSize = 500; // ✅ Augmenté à 500
      let hasMore = true;
      let batchNumber = 0;

      console.log('📦 [VRAIES DONNÉES] Début récupération complète des tâches (batch de 500)...');

      // Estimation du nombre total de batches (pour la progression)
      const estimatedTotal = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        [
          ...tasksQueries,
          Query.limit(1)
        ]
      );
      const estimatedBatches = Math.ceil((estimatedTotal.total || 0) / batchSize);

      while (hasMore) {
        try {
          batchNumber++;
          console.log(`📦 [BATCH ${batchNumber}] Récupération tâches ${offset} à ${offset + batchSize}...`);
          
          const progressPercent = 15 + ((batchNumber / estimatedBatches) * 40); // 15% à 55%
          updateProgress('tasks', progressPercent, `Récupération des tâches - Lot ${batchNumber}/${estimatedBatches}`, batchNumber, estimatedBatches);
          
          const batchResponse = await databases.listDocuments(
            appwriteDatabaseId,
            'audit_tasks',
            [
              ...tasksQueries,
              Query.limit(batchSize),
              Query.offset(offset)
            ]
          );

          const batchTasks = batchResponse.documents;
          allTasks.push(...batchTasks);
          
          console.log(`✅ [BATCH] ${batchTasks.length} tâches récupérées (total: ${allTasks.length})`);
          
          // Vérifier s'il y a encore des données
          if (batchTasks.length < batchSize || offset + batchSize >= (batchResponse.total || 0)) {
            hasMore = false;
          } else {
            offset += batchSize;
            // Délai réduit pour batch plus gros
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        } catch (batchError) {
          console.error(`❌ [BATCH ERROR] Erreur lot ${batchNumber}:`, batchError);
          hasMore = false;
        }
      }

      console.log(`🎯 [TOTAL TÂCHES] ${allTasks.length} tâches récupérées pour la période`);
      updateProgress('tasks', 55, `${allTasks.length} tâches récupérées`);

      // 3. Récupérer toutes les sessions de la période
      updateProgress('sessions', 60, 'Récupération des sessions...');
      
      const sessionsQueries = [
        Query.greaterThanEqual('$createdAt', dateFrom + 'T00:00:00.000Z'),
        Query.lessThanEqual('$createdAt', dateTo + 'T23:59:59.999Z'),
        Query.select(['$id', '$createdAt', 'auditor_id', 'session_status'])
      ];

      if (auditorId && auditorId !== 'all') {
        sessionsQueries.push(Query.equal('auditor_id', auditorId));
      }

      // Récupération complète des sessions (batch de 500)
      let allSessions: any[] = [];
      offset = 0;
      hasMore = true;
      batchNumber = 0;

      console.log('📦 [VRAIES DONNÉES] Début récupération complète des sessions...');

      while (hasMore) {
        try {
          batchNumber++;
          const progressPercent = 60 + ((batchNumber / 5) * 10); // Estimation 5 batches max pour sessions
          updateProgress('sessions', progressPercent, `Récupération des sessions - Lot ${batchNumber}`, batchNumber);
          
          const batchResponse = await databases.listDocuments(
            appwriteDatabaseId,
            'audit_sessions',
            [
              ...sessionsQueries,
              Query.limit(batchSize),
              Query.offset(offset)
            ]
          );

          const batchSessions = batchResponse.documents;
          allSessions.push(...batchSessions);
          
          if (batchSessions.length < batchSize || offset + batchSize >= (batchResponse.total || 0)) {
            hasMore = false;
          } else {
            offset += batchSize;
            await new Promise(resolve => setTimeout(resolve, 30));
          }
        } catch (batchError) {
          console.error('❌ [SESSION BATCH ERROR]:', batchError);
          hasMore = false;
        }
      }

      console.log(`🎯 [TOTAL SESSIONS] ${allSessions.length} sessions récupérées pour la période`);
      updateProgress('sessions', 70, `${allSessions.length} sessions récupérées`);

      // 4. Traitement des données pour créer les rapports journaliers
      updateProgress('processing', 75, 'Traitement des données...');
      
      const reportsMap = new Map<string, DailyReport>();

      // Générer toutes les dates de la période
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);
      const currentDate = new Date(startDate);
      
      const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      let processedDays = 0;

      console.log('📊 [TRAITEMENT] Génération des rapports journaliers...');

      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        processedDays++;
        
        const processingProgress = 75 + ((processedDays / totalDays) * 20); // 75% à 95%
        updateProgress('processing', processingProgress, `Traitement ${dateStr} (${processedDays}/${totalDays})`);
        
        // Pour chaque auditeur, créer une entrée pour cette date
        auditors.forEach(auditor => {
          // Filtrer si un auditeur spécifique est sélectionné
          if (auditorId && auditorId !== 'all' && auditor.$id !== auditorId) {
            return;
          }

          const key = `${dateStr}-${auditor.$id}`;
          
          // ✅ TÂCHES RÉELLES de cet auditeur pour cette date
          const dayTasks = allTasks.filter(task => {
            if (!task.audited_at || !task.auditor_id) return false;
            const taskDate = task.audited_at.split('T')[0];
            return taskDate === dateStr && task.auditor_id === auditor.$id; 
          });
          
          // ✅ SESSIONS RÉELLES de cet auditeur pour cette date
          const daySessions = allSessions.filter(session => {
            if (!session.$createdAt || !session.auditor_id) return false;
            const sessionDate = session.$createdAt.split('T')[0];
            return sessionDate === dateStr && session.auditor_id === auditor.$id;
          });
          
          // ✅ CALCULS RÉELS pour cette date/auditeur
          const totalAudits = dayTasks.length;
          
          // 🎯 DÉFINITION CORRECTE DES AUDITS :
          // audits_count = tâches avec audit_status = 'approved' OU 'rejected'
          // (c'est-à-dire les tâches traitées/auditées)
          const auditedTasks = dayTasks.filter(t => 
            t.audit_status === 'approved' || t.audit_status === 'rejected'
          );
          
          const validatedTasks = dayTasks.filter(t => t.audit_status === 'approved').length;
const commentedTasks = dayTasks.filter(t => t.audit_status === 'rejected').length;
const pendingTasks = dayTasks.filter(t => t.audit_status === 'pending').length;

const processedTasks = validatedTasks + commentedTasks;
const validationRate = processedTasks > 0 ? Math.round((validatedTasks / processedTasks) * 100) : 0;

// Temps de traitement moyen réel
const tasksWithTime = dayTasks.filter(t => t.processing_time_seconds && t.processing_time_seconds > 0);
const avgProcessingTime = tasksWithTime.length > 0
  ? Math.round(tasksWithTime.reduce((sum, t) => sum + (t.processing_time_seconds || 0), 0) / tasksWithTime.length / 60)
  : 0;

// Score de productivité basé sur les vraies données
const productivityScore = Math.min(100, Math.max(0, 
  (totalAudits * 10) + (validationRate * 0.3) + (daySessions.length * 8) // ← Utiliser totalAudits ici aussi
));

// ✅ CRÉER LE RAPPORT SEULEMENT S'IL Y A DE L'ACTIVITÉ RÉELLE
if (totalAudits > 0 || daySessions.length > 0) {
  reportsMap.set(key, {
    date: dateStr,
    auditor_id: auditor.$id,
    auditor_name: auditor.full_name || 'Inconnu',
    audits_count: totalAudits, // ✅ CORRECTION : Utiliser totalAudits au lieu de auditedTasks.length
    sessions_count: daySessions.length,
    validated_tasks: validatedTasks,
    commented_tasks: commentedTasks,
    pending_tasks: pendingTasks,
    avg_processing_time: avgProcessingTime,
    validation_rate: validationRate,
    productivity_score: Math.round(productivityScore)
  });

  // Debug pour les premiers rapports
  if (reportsMap.size <= 3) {
    console.log(`📊 [DEBUG RAPPORT CORRIGÉ] ${dateStr} - ${auditor.full_name}:`, {
      totalTasks: totalAudits,
      auditsComptés: totalAudits, // ← Maintenant c'est cohérent
      validées: validatedTasks,
      commentées: commentedTasks,
      sessions: daySessions.length
    });
  }
}
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      updateProgress('finalizing', 95, 'Finalisation des rapports...');

      const reports = Array.from(reportsMap.values());
      console.log(`✅ [FINAL] ${reports.length} rapports journaliers générés avec vraies données`);
      
      if (reports.length > 0) {
        const totalAudits = reports.reduce((sum, r) => sum + r.audits_count, 0);
        const totalSessions = reports.reduce((sum, r) => sum + r.sessions_count, 0);
        console.log(`📈 [RÉSUMÉ] Total audits: ${totalAudits}, Total sessions: ${totalSessions}`);
      }

      setDailyReports(reports);
      updateProgress('complete', 100, `${reports.length} rapports générés avec succès`);
      
      // Petit délai pour voir la progression complète
      setTimeout(() => {
        setLoading(false);
      }, 500);

    } catch (error) {
      console.error('❌ [ERREUR GÉNÉRALE] Chargement rapports journaliers:', error);
      setError(error instanceof Error ? error.message : 'Erreur inconnue');
      setLoading(false);
      updateProgress('error', 0, 'Erreur lors du chargement');
    }
  }, [dateFrom, dateTo, auditorId, updateProgress]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      loadDailyReports();
    }
  }, [loadDailyReports]);

  return {
    dailyReports,
    loading,
    error,
    loadingProgress,
    refetch: loadDailyReports
  };
};